import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useEtablissement } from '@/contexts/EtablissementContext';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface AddMembreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal d'ajout individuel d'un membre
 * Correspond au mockup HTML : champs + toggles actions automatiques + upload optionnel
 */
export const AddMembreDialog = ({ open, onOpenChange }: AddMembreDialogProps) => {
  const { etablissement } = useEtablissement();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    poste: '',
    type_contrat: 'CDI' as string,
    date_embauche: '',
  });
  
  const [sendAttestation, setSendAttestation] = useState(true);
  const [launchReference, setLaunchReference] = useState(false);
  const [attestationFile, setAttestationFile] = useState<File | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const etablissementId = etablissement?.id;
  const fieldClassName =
    'h-11 w-full rounded-[14px] border border-[hsl(var(--app-border-subtle))] bg-white px-3.5 text-sm text-[hsl(var(--app-text))] outline-none transition placeholder:text-[hsl(var(--app-text-muted))] focus:border-primary focus:ring-4 focus:ring-primary/10';
  const sectionLabelClassName =
    'mb-1.5 block text-[13px] font-medium text-[hsl(var(--app-text-strong))]';

  const handleSubmit = async () => {
    if (!etablissementId) {
      toast({ title: 'Erreur', description: 'Établissement non trouvé', variant: 'destructive' });
      return;
    }
    
    if (!formData.prenom || !formData.nom || !formData.email) {
      toast({ title: 'Champs requis', description: 'Prénom, nom et email sont obligatoires', variant: 'destructive' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // 1. Créer le membre en base
      const { data: membre, error } = await supabase
        .from('membres')
        .insert({
          etablissement_id: etablissementId,
          prenom: formData.prenom,
          nom: formData.nom,
          email: formData.email,
          poste: formData.poste || null,
          type_contrat: formData.type_contrat,
          date_embauche: formData.date_embauche || null,
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // 2. Créer une attestation en_attente
      if ((sendAttestation || attestationFile) && membre) {
        const { data: attestation, error: attError } = await supabase
          .from('attestations')
          .insert({
            membre_id: membre.id,
            etablissement_id: etablissementId,
            statut: attestationFile ? 'en_attente_verification' : 'en_attente',
          })
          .select()
          .single();

        if (attError) {
          console.error('Erreur création attestation:', attError);
        }

        // 3. Upload du fichier si présent
        if (attestationFile && attestation) {
          const fileExt = attestationFile.name.split('.').pop();
          const storagePath = `${etablissementId}/${membre.id}/${Date.now()}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('attestations')
            .upload(storagePath, attestationFile, {
              contentType: attestationFile.type,
              upsert: false,
            });

          if (uploadError) {
            console.error('Erreur upload attestation:', uploadError);
            toast({
              title: 'Upload échoué',
              description: "Le membre a été créé mais l'attestation n'a pas pu être uploadée.",
              variant: 'destructive',
            });
          } else {
            // Mettre à jour la row attestation avec le path et le nom de fichier.
            // received_at = date de réception réelle : la row vient d'être créée
            // juste avant (INSERT ci-dessus), donc pas de risque d'écraser une
            // valeur existante ici.
            await supabase
              .from('attestations')
              .update({
                storage_path: storagePath,
                nom_fichier: attestationFile.name,
                received_at: new Date().toISOString(),
              })
              .eq('id', attestation.id);

            // Déclencher le traitement IA de l'attestation
            supabase.functions.invoke('process-attestation', {
              body: {
                attestation_id: attestation.id,
                storage_path: storagePath,
                membre_id: membre.id,
                etablissement_id: etablissementId,
              },
            }).catch(err => {
              console.error('Erreur process-attestation (non-bloquant):', err);
            });
          }
        }

        // 4. Envoyer l'email de demande d'attestation si toggle activé
        if (sendAttestation && !attestationFile) {
          supabase.functions.invoke('send-attestation-request', {
            body: {
              membre_id: membre.id,
              email: formData.email,
              prenom: formData.prenom,
              nom: formData.nom,
              etablissement_id: etablissementId,
              type: 'demande_initiale',
            },
          }).catch(err => {
            console.error('Erreur envoi email attestation (non-bloquant):', err);
          });
        }
      }
      
      // 5. Invalider le cache des membres
      queryClient.invalidateQueries({ queryKey: ['membres'] });
      
      toast({
        title: 'Membre ajouté',
        description: `${formData.prenom} ${formData.nom} a été ajouté à l'équipe.${attestationFile ? ' Attestation en cours de vérification.' : ''}`,
      });
      
      // Si toggle prise de référence activé → rediriger vers /create-campaign
      if (launchReference) {
        onOpenChange(false);
        navigate(`/create-campaign?prenom=${encodeURIComponent(formData.prenom)}&nom=${encodeURIComponent(formData.nom)}&email=${encodeURIComponent(formData.email)}&poste=${encodeURIComponent(formData.poste)}&source=equipe`);
        return;
      }
      
      // Reset et fermer
      resetForm();
      onOpenChange(false);
      
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.message || "Impossible d'ajouter le membre",
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ prenom: '', nom: '', email: '', poste: '', type_contrat: 'CDI', date_embauche: '' });
    setSendAttestation(true);
    setLaunchReference(false);
    setAttestationFile(null);
    setShowUpload(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="app-content-centered-dialog max-h-[88vh] max-w-[720px] overflow-y-auto rounded-[24px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface))] p-0 shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <DialogHeader className="border-b border-[hsl(var(--app-border-subtle))] px-6 pb-5 pt-6">
          <DialogTitle className="text-[20px] font-semibold tracking-[-0.02em] text-[hsl(var(--app-text-strong))]">
            Ajouter un membre de l'équipe
          </DialogTitle>
          <DialogDescription className="mt-1 text-[14px] leading-6 text-[hsl(var(--app-text-muted))]">
            Le membre recevra un email avec les instructions pour fournir son attestation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 px-6 py-5">
          {/* Prénom / Nom */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={sectionLabelClassName}>Prénom</label>
              <input
                type="text"
                value={formData.prenom}
                onChange={e => setFormData(d => ({ ...d, prenom: e.target.value }))}
                className={fieldClassName}
                placeholder="Marie"
              />
            </div>
            <div>
              <label className={sectionLabelClassName}>Nom</label>
              <input
                type="text"
                value={formData.nom}
                onChange={e => setFormData(d => ({ ...d, nom: e.target.value }))}
                className={fieldClassName}
                placeholder="Dupont"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className={sectionLabelClassName}>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => setFormData(d => ({ ...d, email: e.target.value }))}
              className={fieldClassName}
              placeholder="marie.dupont@email.com"
            />
            <p className="mt-1.5 text-[12px] text-[hsl(var(--app-text-muted))]">
              Pour envoyer automatiquement la demande d'attestation
            </p>
          </div>

          {/* Poste + Type contrat */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={sectionLabelClassName}>Poste / Rôle</label>
              <input
                type="text"
                value={formData.poste}
                onChange={e => setFormData(d => ({ ...d, poste: e.target.value }))}
                className={fieldClassName}
                placeholder="Éducatrice"
              />
            </div>
            <div>
              <label className={sectionLabelClassName}>Type de contrat</label>
              <select
                value={formData.type_contrat}
                onChange={e => setFormData(d => ({ ...d, type_contrat: e.target.value }))}
                className={`${fieldClassName} appearance-none`}
              >
                <option value="CDI">CDI</option>
                <option value="CDD">CDD</option>
                <option value="Benevole">Bénévole</option>
                <option value="Stage">Stagiaire</option>
                <option value="Interim">Intérim</option>
              </select>
            </div>
          </div>

          {/* Date embauche */}
          <div>
            <label className={sectionLabelClassName}>
              Date d'embauche / début d'intervention
            </label>
            <input
              type="date"
              value={formData.date_embauche}
              onChange={e => setFormData(d => ({ ...d, date_embauche: e.target.value }))}
              className={fieldClassName}
            />
          </div>

          {/* Toggles */}
          <div className="space-y-3 border-t border-[hsl(var(--app-border-subtle))] pt-4">
            <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
              Actions automatiques au moment de l'ajout
            </p>
            
            <div className="app-surface-muted rounded-[18px] p-3.5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[14px] font-medium text-[hsl(var(--app-text-strong))]">
                    Envoyer la demande d'attestation d'honorabilité
                  </p>
                  <p className="mt-1 text-[12px] leading-5 text-[hsl(var(--app-text-muted))]">
                    Un email est envoyé avec les instructions pour obtenir l'attestation
                  </p>
                </div>
                <Switch checked={sendAttestation} onCheckedChange={setSendAttestation} />
              </div>
            </div>
            
            <div className="app-surface-muted rounded-[18px] p-3.5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[14px] font-medium text-[hsl(var(--app-text-strong))]">
                    Lancer une prise de référence
                  </p>
                  <p className="mt-1 text-[12px] leading-5 text-[hsl(var(--app-text-muted))]">
                    Recommandé pour les CDI et CDD, pas nécessaire pour les bénévoles
                  </p>
                </div>
                <Switch checked={launchReference} onCheckedChange={setLaunchReference} />
              </div>
            </div>
          </div>

          {/* Zone upload attestation optionnelle — collapsible */}
          <div className="border-t border-[hsl(var(--app-border-subtle))] pt-4">
            <button
              type="button"
              onClick={() => setShowUpload(!showUpload)}
              className="flex items-center gap-2 text-[14px] font-medium text-[hsl(var(--app-text-strong))] transition-colors hover:text-[hsl(var(--app-text))]"
            >
              <span className={`text-xs transition-transform ${showUpload ? 'rotate-90' : ''}`}>▶</span>
              Vous avez déjà l'attestation ? Uploadez-la directement
            </button>
            
            {showUpload && (
              <div className="mt-3 rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] p-3.5">
                <label className="flex h-28 w-full cursor-pointer flex-col items-center justify-center rounded-[16px] border border-dashed border-[hsl(var(--app-border-strong))] bg-white transition-colors hover:border-primary hover:bg-[hsl(var(--app-accent-soft))]">
                  <div className="flex flex-col items-center">
                    <span className="text-lg">📎</span>
                    <p className="mt-1 text-[13px] text-[hsl(var(--app-text))]">
                      Glissez l'attestation ici ou <span className="font-medium text-[hsl(var(--app-accent-strong))]">parcourir</span>
                    </p>
                    <p className="mt-0.5 text-[12px] text-[hsl(var(--app-text-muted))]">
                      PDF · JPG · PNG — L'IA extraira automatiquement les dates
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setAttestationFile(file);
                    }}
                  />
                </label>
                {attestationFile && (
                  <div className="mt-3 flex items-center gap-2 rounded-[14px] border border-[hsl(var(--app-border-subtle))] bg-white p-3">
                    <span className="flex-1 truncate text-[13px] text-[hsl(var(--app-text))]">{attestationFile.name}</span>
                    <button 
                      type="button"
                      onClick={() => setAttestationFile(null)}
                      className="text-[12px] font-medium text-rose-600 transition-colors hover:text-rose-700"
                    >
                      Supprimer
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-[hsl(var(--app-border-subtle))] px-6 py-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="rounded-[14px] border-[hsl(var(--app-border-subtle))]"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.prenom || !formData.nom || !formData.email}
            className="rounded-[14px] px-4"
          >
            {isSubmitting ? 'Ajout en cours...' : 'Ajouter et envoyer la demande'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
